import { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useInstructorCurricula, useReorderCurricula, InstructorCurriculum } from "@/hooks/useCurricula";
import { useToast } from "@/hooks/use-toast";
import { GripVertical, Save, GraduationCap, BookOpen, Users } from "lucide-react";

interface SortableCurriculumItemProps {
  curriculum: InstructorCurriculum;
}

function SortableCurriculumItem({ curriculum }: SortableCurriculumItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: curriculum.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-4 bg-card border rounded-lg ${
        isDragging ? "opacity-50 shadow-lg" : ""
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
      >
        <GripVertical className="h-5 w-5 text-muted-foreground" />
      </button>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{curriculum.title}</span>
          <Badge variant={curriculum.is_published ? "default" : "secondary"} className="shrink-0">
            {curriculum.is_published ? "Published" : "Draft"}
          </Badge>
        </div>
        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <BookOpen className="h-3 w-3" />
            {curriculum.courses_count || 0} courses
          </span>
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {curriculum.enrolled_count || 0} enrolled
          </span>
        </div>
      </div>
    </div>
  );
}

export function CurriculumReorderManager() {
  const { data: curricula, isLoading } = useInstructorCurricula();
  const reorderMutation = useReorderCurricula();
  const { toast } = useToast();
  
  const [orderedCurricula, setOrderedCurricula] = useState<InstructorCurriculum[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (curricula) {
      setOrderedCurricula(curricula);
      setHasChanges(false);
    }
  }, [curricula]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOrderedCurricula((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        setHasChanges(true);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveOrder = async () => {
    const updates = orderedCurricula.map((curriculum, index) => ({
      id: curriculum.id,
      sequence_order: index + 1,
    }));

    try {
      await reorderMutation.mutateAsync(updates);
      toast({
        title: "Order saved",
        description: "Curriculum display order has been updated.",
      });
      setHasChanges(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save curriculum order.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            Curriculum Display Order
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!orderedCurricula || orderedCurricula.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            Curriculum Display Order
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No curricula available to reorder.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5" />
          Curriculum Display Order
        </CardTitle>
        {hasChanges && (
          <Button
            onClick={handleSaveOrder}
            disabled={reorderMutation.isPending}
            size="sm"
          >
            <Save className="h-4 w-4 mr-2" />
            {reorderMutation.isPending ? "Saving..." : "Save Order"}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Drag and drop to reorder how curricula appear to users. Changes are saved when you click "Save Order".
        </p>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedCurricula.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {orderedCurricula.map((curriculum) => (
                <SortableCurriculumItem
                  key={curriculum.id}
                  curriculum={curriculum}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </CardContent>
    </Card>
  );
}
